// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — panel PEMM-Empresa (Hammer · 0743, shell BPMM · 0749)
// Capacidad de transformación de la ORGANIZACIÓN: 13 capacidades en 4 categorías ×
// E1-E4. UNA evaluación por organización (no por proceso). Layout 2 columnas espejo
// del BPMM/PEMM-Proceso (0746):
//   IZQ: heatmap 13×4 SOLO LECTURA (el cuestionario es el input primario) + radar
//        4 categorías + timeline evolución + conclusión del ingeniero (editable).
//   DER: cuestionario colapsable acotado a la altura de la izquierda (scroll interno).
// Global = ESLABÓN DÉBIL (MIN) lo calcula el backend. Debajo, a todo el ancho:
// análisis V·IA + exportación. Consume /process/madurez/empresa/*.
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import {
  fetchEvaluacionesEmpresa,
  crearEvaluacionEmpresa,
  sembrarCapacidadesEmpresa,
  fetchHeatmapEmpresa,
  guardarObservacionesEmpresa,
  aprobarEvaluacionEmpresa,
  type EvaluacionEmpresa,
  type HeatmapEmpresa,
  type NivelEmpresa,
} from './api-pemm-empresa';
import {
  NIVELES_EMPRESA,
  CATEGORIAS_EMPRESA,
  CAPACIDADES_EMPRESA,
  METODO_EMPRESA,
  descriptorCapacidad,
  type ENivel,
  type CategoriaEmpresa,
} from './descriptores-pemm-empresa';
import { CuestionarioEmpresaForm } from './CuestionarioEmpresaForm';
import { MadurezPemmEmpresaAnalisisIA } from './MadurezPemmEmpresaAnalisisIA';
import { capacidadDePregunta } from './preguntas-pemm-empresa';

const C = { a1: '#1F3864', a2: '#2F5496', go: '#595959', gm: '#D9D9D9', gc: '#F2F2F2', w: '#FFF' };
const VERDE = '#16A34A';
const AMBAR = '#E0A800';
const ROJO = '#DC2626';

const NIVELES: NivelEmpresa[] = ['E1', 'E2', 'E3', 'E4'];
const NIVEL_NUM: Record<NivelEmpresa, number> = { E1: 1, E2: 2, E3: 3, E4: 4 };
const hexNivel = (n: NivelEmpresa): string => {
  const v = NIVEL_NUM[n];
  return v >= 3 ? VERDE : v >= 2 ? AMBAR : ROJO;
};
const etqNivel = (n: NivelEmpresa): string =>
  NIVELES_EMPRESA.find((x) => x.nivel === n)?.etiqueta ?? n;
const nombreNivel = (n: NivelEmpresa): string =>
  NIVELES_EMPRESA.find((x) => x.nivel === n)?.nombre ?? '';

const cardStyle: React.CSSProperties = {
  background: C.w,
  border: `0.5px solid ${C.gm}`,
  borderTop: `2px solid ${C.a1}`,
  borderRadius: 8,
  padding: '14px 16px',
};
const SecTitle = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 800,
      color: C.a1,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      paddingBottom: 4,
      borderBottom: `1.5px solid ${C.a1}`,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);
const btn = (bg: string, color: string, border?: string): React.CSSProperties => ({
  fontSize: 12,
  fontWeight: 700,
  padding: '6px 12px',
  borderRadius: 6,
  border: border ?? 'none',
  background: bg,
  color,
  cursor: 'pointer',
  fontFamily: "'DM Sans',sans-serif",
});
const selectStyle: React.CSSProperties = {
  fontSize: 13,
  padding: '6px 10px',
  borderRadius: 6,
  border: `1px solid ${C.gm}`,
  background: C.w,
  color: C.a1,
  fontFamily: "'DM Sans',sans-serif",
  fontWeight: 600,
  minWidth: 240,
};
const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 70,
  resize: 'vertical',
  fontSize: 12.5,
  padding: '8px 10px',
  borderRadius: 6,
  border: `1px solid ${C.gm}`,
  fontFamily: "'DM Sans',sans-serif",
  color: C.a1,
  boxSizing: 'border-box',
};

export function MadurezPemmEmpresaPanel() {
  const { t } = useI18n();
  const k = ((t.process as Record<string, unknown>)?.madurezEmpresa ?? {}) as Record<
    string,
    string
  >;
  const tx = (key: string, fallback: string): string => k[key] ?? fallback;

  const [evals, setEvals] = useState<EvaluacionEmpresa[]>([]);
  const [evalId, setEvalId] = useState<string>('');
  const [heatmap, setHeatmap] = useState<HeatmapEmpresa | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCap, setSelectedCap] = useState<string | null>(null);
  const [conclusion, setConclusion] = useState('');
  const [savingConc, setSavingConc] = useState(false);
  const [concSaved, setConcSaved] = useState(false);

  useEffect(() => {
    fetchEvaluacionesEmpresa()
      .then((r) => {
        const lista = r.data ?? [];
        setEvals(lista);
        setEvalId(lista[0]?.id ?? '');
        if (!lista.length) setHeatmap(null);
      })
      .catch(() => setError(tx('errorEvals', 'No se pudieron cargar las evaluaciones')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarHeatmap = useCallback((id: string) => {
    if (!id) {
      setHeatmap(null);
      return;
    }
    setLoading(true);
    fetchHeatmapEmpresa(id)
      .then((r) => setHeatmap(r.data))
      .catch(() => setError(tx('errorHeatmap', 'No se pudo cargar el heatmap')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    cargarHeatmap(evalId);
  }, [evalId, cargarHeatmap]);

  const ev = heatmap?.evaluacion ?? null;
  const readOnly = ev?.estado === 'APROBADA' || ev?.estado === 'OBSOLETA';
  const nivelGlobal = ev?.nivelGlobal ?? null;
  const nivelGlobalNum = nivelGlobal ? NIVEL_NUM[nivelGlobal] : 0;
  const celdas = new Map((heatmap?.capacidades ?? []).map((c) => [c.capacidad, c]));
  const tieneSiembra = (heatmap?.capacidades.length ?? 0) > 0;

  // 📝 también cuando hay notas del cuestionario para la capacidad (0753):
  // las notas por pregunta viven en respuestas jsonb, no en observaciones de celda.
  const tieneNotaCuestionario = (clave: string): boolean =>
    Object.entries(ev?.respuestas ?? {}).some(
      ([k, v]) => capacidadDePregunta(k) === clave && !!v?.nota,
    );

  // Sincronizar conclusión del ingeniero al cambiar de evaluación.
  const obsActual = ev?.observaciones ?? '';
  useEffect(() => {
    setConclusion(obsActual);
    setConcSaved(false);
  }, [evalId, obsActual]);

  const crear = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await crearEvaluacionEmpresa();
      await sembrarCapacidadesEmpresa(data.id);
      const r = await fetchEvaluacionesEmpresa();
      setEvals(r.data ?? []);
      setEvalId(data.id);
    } catch {
      setError(tx('errorCrear', 'No se pudo crear la evaluación (¿permiso de edición?)'));
    } finally {
      setBusy(false);
    }
  };

  const sembrar = async () => {
    if (!evalId) return;
    setBusy(true);
    setError(null);
    try {
      await sembrarCapacidadesEmpresa(evalId);
      cargarHeatmap(evalId);
    } catch {
      setError(tx('errorSembrar', 'No se pudieron sembrar las capacidades'));
    } finally {
      setBusy(false);
    }
  };

  const guardarConclusion = async () => {
    if (!evalId || readOnly) return;
    setSavingConc(true);
    setConcSaved(false);
    setError(null);
    try {
      await guardarObservacionesEmpresa(evalId, conclusion);
      setConcSaved(true);
      cargarHeatmap(evalId);
    } catch {
      setError(tx('errorConclusion', 'No se pudo guardar la conclusión'));
    } finally {
      setSavingConc(false);
    }
  };

  const aprobar = async () => {
    if (!evalId) return;
    setBusy(true);
    setError(null);
    try {
      await aprobarEvaluacionEmpresa(evalId);
      const r = await fetchEvaluacionesEmpresa();
      setEvals(r.data ?? []);
      cargarHeatmap(evalId);
    } catch {
      setError(tx('errorAprobar', 'No se pudo aprobar (¿rol directivo?)'));
    } finally {
      setBusy(false);
    }
  };

  // Nivel de cada categoría = eslabón débil (MIN) de sus capacidades.
  const nivelCategoria = (cat: CategoriaEmpresa): number => {
    const nums = CAPACIDADES_EMPRESA.filter((c) => c.categoria === cat)
      .map((c) => celdas.get(c.clave)?.nivel)
      .filter((n): n is NivelEmpresa => !!n)
      .map((n) => NIVEL_NUM[n]);
    return nums.length ? Math.min(...nums) : 0;
  };
  const radarData = CATEGORIAS_EMPRESA.map((c) => ({
    categoria: c.nombre,
    nivel: nivelCategoria(c.categoria),
  }));

  return (
    <div
      style={{
        maxWidth: 1400,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '10px 14px',
        background: 'var(--wl-background, #ECEEF2)',
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      {/* ── Barra de control: evaluación + global ────────────────────────────── */}
      <div style={cardStyle}>
        <SecTitle>
          {tx('titulo', 'Madurez de la empresa · PEMM (capacidad de transformación)')}
        </SecTitle>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          {evals.length > 0 && (
            <select style={selectStyle} value={evalId} onChange={(e) => setEvalId(e.target.value)}>
              {evals.map((e) => (
                <option key={e.id} value={e.id}>
                  {(e.codigo ?? '—') + ' · ' + e.fechaEvaluacion.slice(0, 10) + ' · ' + e.estado}
                </option>
              ))}
            </select>
          )}
          <button
            style={{ ...btn(C.a1, C.w), opacity: busy ? 0.6 : 1 }}
            onClick={crear}
            disabled={busy}
          >
            {tx('nueva', '+ Nueva evaluación')}
          </button>
          {ev && nivelGlobal && (
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: C.gc,
                borderRadius: 8,
                padding: '8px 14px',
                border: `1px solid ${C.gm}`,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, color: C.go }}>
                {tx('global', 'Nivel de la empresa')}
              </span>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: hexNivel(nivelGlobal),
                  display: 'inline-block',
                }}
              />
              <strong style={{ fontSize: 15, color: C.a1 }}>
                {etqNivel(nivelGlobal)} · {nombreNivel(nivelGlobal)}
              </strong>
            </div>
          )}
        </div>
        <p style={{ fontSize: 11, color: C.go, margin: '10px 0 0', lineHeight: 1.5 }}>
          {METODO_EMPRESA}
        </p>
      </div>

      {error && (
        <div
          style={{
            ...cardStyle,
            borderTop: `2px solid ${ROJO}`,
            color: ROJO,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}

      {loading && (
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <LoadingSpinner />
        </div>
      )}

      {/* ── Estados vacíos ──────────────────────────────────────────────────── */}
      {!loading && evals.length === 0 && (
        <div style={{ ...cardStyle, color: C.go, fontSize: 14 }}>
          {tx(
            'vacio',
            'Aún no hay evaluación de capacidad de transformación. Crea la primera con «+ Nueva evaluación».',
          )}
        </div>
      )}
      {!loading && ev && !tieneSiembra && (
        <div style={cardStyle}>
          <p style={{ fontSize: 14, color: C.go, margin: '0 0 10px' }}>
            {tx(
              'sinSiembra',
              'La evaluación no tiene capacidades. Siembra las 13 en E-1 para empezar el audit.',
            )}
          </p>
          <button
            style={{ ...btn(C.a2, C.w), opacity: busy ? 0.6 : 1 }}
            onClick={sembrar}
            disabled={busy}
          >
            {tx('sembrar', 'Sembrar 13 capacidades')}
          </button>
        </div>
      )}

      {/* ── Layout 2 columnas ───────────────────────────────────────────────── */}
      {!loading && ev && tieneSiembra && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr',
            gap: 10,
            alignItems: 'stretch',
            flexShrink: 0,
          }}
        >
          {/* ── IZQUIERDA ──────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
            {/* Heatmap 13×4 (solo lectura: el cuestionario es el input primario) */}
            <div style={cardStyle}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <SecTitle>{tx('heatmap', 'Matriz de capacidades 13 × 4')}</SecTitle>
                {!readOnly ? (
                  <button
                    style={{ ...btn(VERDE, C.w), opacity: busy ? 0.6 : 1 }}
                    onClick={aprobar}
                    disabled={busy}
                  >
                    {tx('aprobar', 'Aprobar evaluación')}
                  </button>
                ) : (
                  <span style={{ fontSize: 11, fontWeight: 700, color: VERDE }}>
                    {tx('aprobada', '✓ Aprobada (solo lectura)')}
                  </span>
                )}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.6fr repeat(4, 1fr)',
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <div />
                {NIVELES.map((n) => (
                  <div
                    key={n}
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: C.a1,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                    }}
                    title={NIVELES_EMPRESA.find((x) => x.nivel === n)?.resumen}
                  >
                    {etqNivel(n)}
                  </div>
                ))}
              </div>

              {CATEGORIAS_EMPRESA.map((cat) => {
                const caps = CAPACIDADES_EMPRESA.filter((c) => c.categoria === cat.categoria);
                return (
                  <div key={cat.categoria} style={{ marginBottom: 6 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: C.a2,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        margin: '6px 0 3px',
                      }}
                    >
                      {cat.nombre}
                    </div>
                    {caps.map((c) => {
                      const celda = celdas.get(c.clave);
                      const nivelActual = celda?.nivel ?? null;
                      const esDebil =
                        nivelActual != null && nivelGlobal != null && nivelActual === nivelGlobal;
                      const seleccionado = selectedCap === c.clave;
                      const tieneNota = !!celda?.observaciones || tieneNotaCuestionario(c.clave);
                      return (
                        <div
                          key={c.clave}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1.6fr repeat(4, 1fr)',
                            gap: 4,
                            marginBottom: 3,
                          }}
                        >
                          <button
                            onClick={() => setSelectedCap(c.clave)}
                            style={{
                              textAlign: 'left',
                              fontSize: 12,
                              fontWeight: seleccionado ? 800 : 600,
                              color: C.a1,
                              background: seleccionado ? C.gc : 'transparent',
                              border: 'none',
                              borderRadius: 4,
                              padding: '0 4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              fontFamily: "'DM Sans',sans-serif",
                            }}
                            title={tx('verDetalle', 'Resaltar en el cuestionario')}
                          >
                            {esDebil && (
                              <span
                                title={tx('eslabon', 'Eslabón débil: marca el nivel de la empresa')}
                              >
                                🔗
                              </span>
                            )}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {c.nombre}
                            </span>
                            {tieneNota && <span title={tx('conNota', 'Tiene nota')}>📝</span>}
                          </button>
                          {NIVELES.map((n) => {
                            const activo = nivelActual === n;
                            const bloqueada = nivelGlobalNum > 0 && NIVEL_NUM[n] > nivelGlobalNum;
                            return (
                              <button
                                key={n}
                                onClick={() => setSelectedCap(c.clave)}
                                title={descriptorCapacidad(c.clave, n as ENivel)}
                                style={{
                                  border: activo ? `2px solid ${C.a1}` : `1px solid ${C.gm}`,
                                  borderRadius: 5,
                                  background: activo ? hexNivel(n) : bloqueada ? '#FBFBFC' : C.w,
                                  color: activo ? C.w : C.go,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  padding: '6px 4px',
                                  cursor: readOnly ? 'default' : 'pointer',
                                  opacity: !activo && bloqueada ? 0.45 : 1,
                                  fontFamily: "'DM Sans',sans-serif",
                                }}
                              >
                                {activo ? etqNivel(n) : ''}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 14,
                  marginTop: 10,
                  paddingTop: 8,
                  borderTop: `1px solid ${C.gm}`,
                }}
              >
                {NIVELES_EMPRESA.map((nv) => (
                  <span
                    key={nv.nivel}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      fontSize: 10,
                      color: C.go,
                    }}
                  >
                    <span
                      style={{
                        width: 11,
                        height: 11,
                        borderRadius: '50%',
                        background: hexNivel(nv.nivel),
                        display: 'inline-block',
                      }}
                    />
                    <strong style={{ color: C.a1 }}>{nv.etiqueta}</strong> {nv.nombre}
                  </span>
                ))}
              </div>
            </div>

            {/* Radar 4 categorías */}
            <div style={cardStyle}>
              <SecTitle>{tx('radar', 'Radar por categorías (eslabón débil)')}</SecTitle>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <RadarChart data={radarData} outerRadius="72%">
                    <PolarGrid />
                    <PolarAngleAxis
                      dataKey="categoria"
                      tick={{ fontSize: 11, fill: C.a1, fontWeight: 700 }}
                    />
                    <PolarRadiusAxis
                      domain={[0, 4]}
                      tickCount={5}
                      tick={{ fontSize: 9, fill: C.go }}
                    />
                    <Radar
                      dataKey="nivel"
                      stroke={C.a2}
                      fill={C.a2}
                      fillOpacity={0.35}
                      isAnimationActive={false}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Timeline evolución */}
            <div style={cardStyle}>
              <SecTitle>{tx('evolucion', 'Evolución de la empresa')}</SecTitle>
              {evals.length <= 1 ? (
                <p style={{ fontSize: 12, color: C.go, margin: 0 }}>
                  {tx('sinEvolucion', 'Con una sola evaluación aún no hay evolución que mostrar.')}
                </p>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  {[...evals].reverse().map((e) => {
                    const ng = e.nivelGlobal;
                    const activo = e.id === evalId;
                    return (
                      <button
                        key={e.id}
                        onClick={() => setEvalId(e.id)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 3,
                          border: activo ? `2px solid ${C.a1}` : `1px solid ${C.gm}`,
                          borderRadius: 6,
                          padding: '6px 8px',
                          background: activo ? C.gc : C.w,
                          cursor: 'pointer',
                          fontFamily: "'DM Sans',sans-serif",
                          minWidth: 64,
                        }}
                        title={e.codigo ?? ''}
                      >
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: ng ? hexNivel(ng) : C.gm,
                          }}
                        />
                        <strong style={{ fontSize: 11, color: C.a1 }}>
                          {ng ? etqNivel(ng) : '—'}
                        </strong>
                        <span style={{ fontSize: 9, color: C.go }}>
                          {e.fechaEvaluacion.slice(0, 10)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Conclusión del ingeniero */}
            <div style={cardStyle}>
              <SecTitle>{tx('conclusion', 'Conclusión del ingeniero de gobernanza')}</SecTitle>
              <textarea
                style={textareaStyle}
                value={conclusion}
                disabled={readOnly}
                placeholder={tx(
                  'conclusionPh',
                  'Síntesis: ¿la empresa puede sostener la mejora de sus procesos? Eslabón(es) débil(es), qué desbloquea el siguiente nivel…',
                )}
                onChange={(e) => {
                  setConclusion(e.target.value);
                  setConcSaved(false);
                }}
              />
              {!readOnly && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                  <button
                    style={{ ...btn(C.a1, C.w), opacity: savingConc ? 0.6 : 1 }}
                    onClick={guardarConclusion}
                    disabled={savingConc}
                  >
                    {savingConc ? tx('guardando', 'Guardando…') : tx('guardar', 'Guardar')}
                  </button>
                  {concSaved && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: VERDE }}>
                      {tx('guardado', '✓ Guardado')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── DERECHA: cuestionario acotado a la altura de la izquierda (scroll interno · patrón BPMM) ── */}
          <div style={{ position: 'relative', minWidth: 0, minHeight: 0 }}>
            <div
              style={{
                ...cardStyle,
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <SecTitle>{tx('cuestionarioTitulo', 'Cuestionario PEMM-Empresa')}</SecTitle>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <CuestionarioEmpresaForm
                  evaluacionId={evalId}
                  respuestas={ev?.respuestas ?? null}
                  readOnly={readOnly}
                  selectedCap={selectedCap}
                  onSelectCap={setSelectedCap}
                  onSaved={(hm) => setHeatmap(hm)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Análisis IA Empresa (full width, bajo las 2 columnas) ────────────── */}
      {!loading && ev && tieneSiembra && (
        <MadurezPemmEmpresaAnalisisIA evaluacionId={evalId} observaciones={conclusion} />
      )}
    </div>
  );
}
